import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getEventById, saveEvent } from "../api/events";
import { getEventTypeFields, getEventTypeFormConfig } from "../config/eventTypeFields";
import { WorkflowSteps } from "../components/WorkflowSteps";
import {
  detectPolotnoEventPlaceholders,
  extractEventDetailsFromPolotno,
} from "../utils/polotnoEventDetailsExtractor";

const SUBEVENT_LINK_PREFIX = "subevent_link_";
const LOCATION_MAP_LINK_PREFIX = "location_map_link_";

const arrayToObject = (metadataArray) =>
  (metadataArray || []).reduce((acc, item) => {
    acc[item.meta_key] = item.meta_value;
    return acc;
  }, {});

const extractLinkedSubevents = (metadataArray) =>
  (metadataArray || []).reduce((acc, item) => {
    if (item?.meta_key?.startsWith(SUBEVENT_LINK_PREFIX)) {
      const subeventId = item.meta_key.replace(SUBEVENT_LINK_PREFIX, "");
      acc[subeventId] = item.meta_value === "previous";
    }
    return acc;
  }, {});

const EVENT_DETAIL_KEYS = ["title", "host_name", "event_date", "event_time", "location"];

const isEmptyValue = (value) => value === null || value === undefined || `${value}`.trim() === "";

const TIME_OPTIONS = Array.from({ length: 40 }, (_, index) => {
  const totalMinutes = 5 * 60 + index * 30;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const value = `${hours}`.padStart(2, "0") + ":" + `${minutes}`.padStart(2, "0");
  const displayHour = hours % 12 || 12;
  const displayMinute = `${minutes}`.padStart(2, "0");
  const period = hours < 12 ? "AM" : "PM";
  return {
    value,
    label: `${displayHour}:${displayMinute} ${period}`,
  };
});

const getPolotnoPageOptions = (entity) => ({
  pageId: entity?.page_id,
  pageIndex: Number.isInteger(entity?.page_index) ? entity.page_index : undefined,
});

const getEntityDetailSuggestions = (entity) =>
  EVENT_DETAIL_KEYS.reduce((acc, key) => {
    if (!isEmptyValue(entity?.[key])) {
      return { ...acc, [key]: entity[key] };
    }
    return acc;
  }, {});

const extractPolotnoSuggestionsForEntity = (entity, fallbackHtmltext = "") => {
  const sourceHtmltext = entity?.htmltext || fallbackHtmltext;
  const pageOptions = getPolotnoPageOptions(entity);
  const suggestions = {
    ...extractEventDetailsFromPolotno(sourceHtmltext, pageOptions),
    ...getEntityDetailSuggestions(entity),
  };
  const placeholders = detectPolotnoEventPlaceholders(sourceHtmltext, pageOptions);

  return { suggestions, placeholders };
};

const applySuggestionsToEntity = (entity, suggestions) => {
  let appliedCount = 0;
  const updatedEntity = EVENT_DETAIL_KEYS.reduce((acc, key) => {
    if (isEmptyValue(acc?.[key]) && !isEmptyValue(suggestions[key])) {
      appliedCount += 1;
      return { ...acc, [key]: suggestions[key] };
    }
    return acc;
  }, entity);

  return { entity: updatedEntity, appliedCount };
};

const findEditableTargetPageIndex = (pages, sourcePageIndex, linkedSubevents) => {
  for (let index = sourcePageIndex - 1; index >= 0; index -= 1) {
    const page = pages[index];
    if (page.type === "event") return index;
    if (!linkedSubevents[String(page.data.id)]) return index;
  }

  return sourcePageIndex;
};

const applyLinkedPageSuggestionsToPreviousForm = ({ eventData, sourcePageIndex, linkedSubevents }) => {
  if (!eventData || sourcePageIndex <= 0) {
    return eventData;
  }

  const fullPolotnoHtmltext = eventData.htmltext;
  const pageEntities = [
    { type: "event", data: eventData },
    ...((eventData.subevents || []).map((subevent) => ({ type: "subevent", data: subevent }))),
  ];
  const sourcePage = pageEntities[sourcePageIndex];

  if (!sourcePage) {
    return eventData;
  }

  const targetPageIndex = findEditableTargetPageIndex(pageEntities, sourcePageIndex, linkedSubevents);
  const sourceSuggestions = extractPolotnoSuggestionsForEntity(sourcePage.data, fullPolotnoHtmltext);

  if (targetPageIndex === 0) {
    const applied = applySuggestionsToEntity(eventData, sourceSuggestions.suggestions);
    return { ...eventData, ...applied.entity };
  }

  const targetSubeventIndex = targetPageIndex - 1;
  const updatedSubevents = [...(eventData.subevents || [])];
  const applied = applySuggestionsToEntity(
    updatedSubevents[targetSubeventIndex],
    sourceSuggestions.suggestions
  );

  updatedSubevents[targetSubeventIndex] = applied.entity;
  return { ...eventData, subevents: updatedSubevents };
};

const applyPolotnoSuggestionsToEvent = (eventData) => {
  if (!eventData) {
    return { event: eventData, metadata: {}, appliedCount: 0 };
  }

  const fullPolotnoHtmltext = eventData.htmltext;
  const linkedSubevents = extractLinkedSubevents(eventData.eventmetadata);
  const pageEntities = [
    { type: "event", data: eventData },
    ...((eventData.subevents || []).map((subevent) => ({ type: "subevent", data: subevent }))),
  ];
  const mainSuggestions = extractPolotnoSuggestionsForEntity(eventData, fullPolotnoHtmltext);
  const mainResult = applySuggestionsToEntity(eventData, mainSuggestions.suggestions);
  const metadata = arrayToObject(eventData.eventmetadata);
  let appliedCount = mainResult.appliedCount;

  const updatedMetadata = Object.entries(mainSuggestions.suggestions).reduce((acc, [key, value]) => {
    if (!EVENT_DETAIL_KEYS.includes(key) && isEmptyValue(acc[key]) && !isEmptyValue(value)) {
      appliedCount += 1;
      return { ...acc, [key]: value };
    }
    return acc;
  }, metadata);

  const updatedSubevents = [...(eventData.subevents || [])];

  pageEntities.slice(1).forEach((page, slicedIndex) => {
    const sourcePageIndex = slicedIndex + 1;
    const result = extractPolotnoSuggestionsForEntity(page.data, fullPolotnoHtmltext);
    const shouldMoveToPreviousForm = Boolean(linkedSubevents[String(page.data.id)]);
    const targetPageIndex = shouldMoveToPreviousForm
      ? findEditableTargetPageIndex(pageEntities, sourcePageIndex, linkedSubevents)
      : sourcePageIndex;

    if (targetPageIndex === 0) {
      const applied = applySuggestionsToEntity(mainResult.entity, result.suggestions);
      Object.assign(mainResult.entity, applied.entity);
      appliedCount += applied.appliedCount;
      return;
    }

    const targetSubeventIndex = targetPageIndex - 1;
    const applied = applySuggestionsToEntity(updatedSubevents[targetSubeventIndex], result.suggestions);
    updatedSubevents[targetSubeventIndex] = applied.entity;
    appliedCount += applied.appliedCount;
  });

  return {
    event: {
      ...mainResult.entity,
      subevents: updatedSubevents,
    },
    metadata: updatedMetadata,
    placeholderCount: mainSuggestions.placeholders.length,
    appliedCount,
  };
};

export const EventManagerPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const eventId = location.state?.eventId;

  const [event, setEvent] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [structuredMetadata, setStructuredMetadata] = useState({});
  const [linkedSubevents, setLinkedSubevents] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveNotice, setSaveNotice] = useState("");
  const [prefillNotice, setPrefillNotice] = useState("");

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) return;
      try {
        const data = await getEventById(eventId);
        const prefilled = applyPolotnoSuggestionsToEvent(data);
        setEvent(prefilled.event);
        setSelectedPage({ type: "event", data: prefilled.event });
        setStructuredMetadata(prefilled.metadata);
        setLinkedSubevents(extractLinkedSubevents(data.eventmetadata));
        setPrefillNotice(
          prefilled.appliedCount
            ? `${prefilled.appliedCount} detail${prefilled.appliedCount === 1 ? "" : "s"} suggested from template text. Review before saving.`
            : prefilled.placeholderCount
              ? `${prefilled.placeholderCount} template placeholder${prefilled.placeholderCount === 1 ? "" : "s"} detected, but no real values were filled. Replace placeholder text in the editor or enter details here.`
            : ""
        );
      } catch (err) {
        console.error("Error fetching event:", err);
      }
    };

    fetchEvent();
  }, [eventId]);

  const pages = useMemo(() => {
    if (!event) return [];
    return [
      { type: "event", data: event },
      ...((event.subevents || []).map((sub) => ({
        type: "subevent",
        data: sub,
      }))),
    ];
  }, [event]);

  const requiredMetadataFields = getEventTypeFields(event);
  const visibleMetadataFields = requiredMetadataFields.filter(
    (field) => !EVENT_DETAIL_KEYS.includes(field.key)
  );
  const eventTypeFormConfig = getEventTypeFormConfig(event);
  const selectedSubeventId = selectedPage?.type === "subevent" ? String(selectedPage.data.id) : null;
  const selectedLocationMapKey = `${LOCATION_MAP_LINK_PREFIX}${selectedSubeventId || "main"}`;
  const isSelectedSubeventLinked = selectedSubeventId ? Boolean(linkedSubevents[selectedSubeventId]) : false;
  const selectedPageIndex = pages.findIndex((page) => page.data.id === selectedPage?.data?.id);
  const previousPage = selectedPageIndex > 0 ? pages[selectedPageIndex - 1] : null;
  const previousPageTitle = previousPage?.data?.title || "previous ceremony";
  const selectedPageLabel =
    selectedPage?.type === "event"
      ? "Main event cover"
      : `Subevent page ${selectedPageIndex >= 0 ? selectedPageIndex + 1 : ""}`.trim();
  const selectedDisplayTitle =
    selectedPage?.type === "subevent" && isSelectedSubeventLinked
      ? `Same as ${previousPageTitle}`
      : selectedPage?.data?.title || "Subevent Details";
  const selectedPageStatus = isSelectedSubeventLinked
    ? "Linked to previous ceremony"
    : selectedPage?.type === "subevent"
      ? "Has its own ceremony details"
      : "Primary event details";
  const eventTypeName =
    event?.event_type_name || event?.event_type?.name || eventTypeFormConfig?.eventTypeLabel || "Invitation";
  const subeventCount = Math.max(pages.length - 1, 0);
  const hasSubevents = subeventCount > 0;
  const locationMaxLength = selectedPage?.type === "subevent" ? 255 : 500;

  const sectionLabelClassName =
    "mb-3 block text-[10px] font-bold uppercase tracking-[0.22em] text-[#b6849f]";
  const fieldLabelClassName =
    "block text-[10px] font-bold uppercase tracking-widest text-gray-400";
  const inputClassName =
    "w-full rounded-xl border border-gray-100 bg-gray-50 p-3 outline-none transition-all focus:border-[#ff0099]";

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (selectedPage?.type === "event") {
      if (EVENT_DETAIL_KEYS.includes(name)) {
        const updatedEvent = { ...event, [name]: value };
        setEvent(updatedEvent);
        setSelectedPage({ type: "event", data: updatedEvent });
      } else if (requiredMetadataFields.some((item) => item.key === name)) {
        setStructuredMetadata((prev) => ({ ...prev, [name]: value }));
      }
    } else if (selectedPage?.type === "subevent") {
      const updatedSub = { ...selectedPage.data, [name]: value };
      const updatedSubevents = event.subevents.map((subevent) =>
        subevent.id === updatedSub.id ? updatedSub : subevent
      );
      const updatedEvent = { ...event, subevents: updatedSubevents };
      setEvent(updatedEvent);
      setSelectedPage({ type: "subevent", data: updatedSub });
    }
  };

  const handleMetadataValueChange = (name, value) => {
    setStructuredMetadata((prev) => ({ ...prev, [name]: value }));
  };

  const handleLinkedSubeventChange = (subeventId, isLinked) => {
    const subeventKey = String(subeventId);
    const nextLinkedSubevents = {
      ...linkedSubevents,
      [subeventKey]: isLinked,
    };

    setLinkedSubevents(nextLinkedSubevents);

    if (!isLinked || !event) {
      return;
    }

    const sourcePageIndex = pages.findIndex(
      (page) => page.type === "subevent" && String(page.data.id) === subeventKey
    );

    if (sourcePageIndex <= 0) {
      return;
    }

    const updatedEvent = applyLinkedPageSuggestionsToPreviousForm({
      eventData: event,
      sourcePageIndex,
      linkedSubevents: nextLinkedSubevents,
    });

    setEvent(updatedEvent);

    if (selectedPage?.data?.id) {
      const refreshedSelectedPage =
        selectedPage.type === "event"
          ? { type: "event", data: updatedEvent }
          : {
              type: "subevent",
              data:
                (updatedEvent.subevents || []).find(
                  (subevent) => subevent.id === selectedPage.data.id
                ) || selectedPage.data,
            };
      setSelectedPage(refreshedSelectedPage);
    }
  };

  const handleSave = async (navigateToNextStep = false) => {
    try {
      if (!event) {
        return;
      }

      setIsSaving(true);
      setSaveError("");
      setSaveNotice("");

      const metadataArray = Object.keys(structuredMetadata)
        .filter((key) => !key.startsWith(SUBEVENT_LINK_PREFIX))
        .filter((key) => structuredMetadata[key])
        .map((key) => ({
          meta_key: key,
          meta_value: structuredMetadata[key],
        }));

      const linkedMetadata = Object.keys(linkedSubevents)
        .filter((subeventIdKey) => linkedSubevents[subeventIdKey])
        .map((subeventIdKey) => ({
          meta_key: `${SUBEVENT_LINK_PREFIX}${subeventIdKey}`,
          meta_value: "previous",
        }));

      const payload = {
        ...event,
        eventmetadata: [...metadataArray, ...linkedMetadata],
      };

      delete payload.owner_id;
      delete payload.created_date;

      const selectedId = selectedPage?.data?.id;
      const selectedType = selectedPage?.type;
      const savedEvent = await saveEvent({ mainEvent: payload, eventId });

      setEvent(savedEvent);
      setStructuredMetadata(arrayToObject(savedEvent.eventmetadata));
      setLinkedSubevents(extractLinkedSubevents(savedEvent.eventmetadata));

      if (selectedType === "subevent" && selectedId) {
        const refreshedSubevent = (savedEvent.subevents || []).find((subevent) => subevent.id === selectedId);
        if (refreshedSubevent) {
          setSelectedPage({ type: "subevent", data: refreshedSubevent });
        }
      } else {
        setSelectedPage({ type: "event", data: savedEvent });
      }

      setSaveNotice("Details saved successfully.");

      if (navigateToNextStep) {
        navigate("/event-theme", { state: { eventId } });
      }
    } catch (err) {
      console.error("Error saving:", err);
      setSaveError(err?.message || "Unable to save event details right now.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!event) return <p>Loading event...</p>;

  return (
    <div className="min-h-screen bg-[#fff4fb] font-sans">
      <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-6 p-4 md:p-8 xl:p-10">
        <WorkflowSteps currentStep="details" />

        <header className="rounded-[32px] border border-[#f3dbe7] bg-white/90 px-6 py-6 shadow-[0_24px_60px_rgba(125,70,105,0.08)] backdrop-blur">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#b07c98]">
                Event Details
              </p>
              <h1 className="text-[clamp(1.35rem,2vw,1.85rem)] font-black leading-[1.02] text-[#2f2030]">
                Add event details and invitation chapters
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-[#806777]">
                Start with the main cover, then review each ceremony or chapter. Later, in Manage
                Guests, you can decide which guests or groups receive which subevent pages.
              </p>
            </div>

            <div className="grid min-w-[220px] grid-cols-2 gap-3 sm:grid-cols-3 lg:min-w-[320px]">
              <div className="rounded-2xl border border-[#f3dce7] bg-[#fff8fc] px-4 py-3">
                <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                  Total Pages
                </span>
                <strong className="mt-1 block text-lg text-[#3a2433]">{pages.length}</strong>
                <span className="mt-1 block text-[11px] font-semibold text-[#9a7186]">
                  {hasSubevents ? `${subeventCount} subevent${subeventCount === 1 ? "" : "s"}` : "Cover only"}
                </span>
              </div>
              <div className="rounded-2xl border border-[#f3dce7] bg-[#fff8fc] px-4 py-3">
                <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                  Reused Details
                </span>
                <strong className="mt-1 block text-lg text-[#3a2433]">
                  {Object.values(linkedSubevents).filter(Boolean).length}
                </strong>
                <span className="mt-1 block text-[11px] font-semibold text-[#9a7186]">
                  linked chapter pages
                </span>
              </div>
              <div className="rounded-2xl border border-[#f3dce7] bg-[#fff8fc] px-4 py-3 col-span-2 sm:col-span-1">
                <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                  Selected
                </span>
                <strong className="mt-1 block truncate text-sm text-[#3a2433] md:text-base">
                  {selectedPageLabel}
                </strong>
              </div>
            </div>
          </div>
        </header>

        <div className="flex flex-col gap-8 xl:flex-row">
      <div className="flex flex-[1.55] flex-col items-center">
        <div className="flex w-full max-w-[860px] flex-col gap-4 md:flex-row md:items-start md:justify-center">
          <div className="rounded-[28px] border border-[#f0dae6] bg-white/90 px-4 py-4 shadow-[0_20px_50px_rgba(125,70,105,0.06)] backdrop-blur md:w-[150px] md:shrink-0">
            <div className="mb-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#b07c98]">
                Chapters
              </p>
              <p className="mt-1 text-xs leading-5 text-[#8d6680]">
                Cover plus subevent pages
              </p>
            </div>

            <div className="no-scrollbar flex gap-4 overflow-x-auto px-2 pb-2 md:max-h-[68vh] md:flex-col md:overflow-y-auto md:overflow-x-visible md:pb-0">
              {pages.map((page, idx) => {
                const isLinkedPage =
                  page.type === "subevent" && Boolean(linkedSubevents[String(page.data.id)]);

                return (
                  <button
                    key={`${page.type}-${page.data.id || idx}`}
                    type="button"
                    onClick={() => setSelectedPage(page)}
                    className="relative shrink-0 border-0 bg-transparent p-0 text-left outline-none focus:outline-none focus:ring-0"
                  >
                    <img
                      src={page.data.image_url}
                      alt="Thumbnail"
                      className={`h-24 w-20 cursor-pointer rounded-xl border-4 object-contain transition-all md:h-28 md:w-24 ${
                        selectedPage?.data?.id === page.data.id
                          ? "scale-105 border-[#ff0099] shadow-lg ring-2 ring-[#ffd2e7]"
                          : "border-transparent opacity-60 hover:opacity-100"
                      }`}
                    />
                    {isLinkedPage ? (
                      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 rounded-full bg-[#7f2556] px-2 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-white shadow-lg">
                        Linked
                      </span>
                    ) : null}
                    <div className="mt-2 text-center">
                      <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                        {page.type === "event" ? "Main Cover" : `Chapter ${idx}`}
                      </span>
                      {isLinkedPage ? (
                        <span className="mt-1 block max-w-24 truncate text-[10px] font-semibold text-[#7f2556]">
                          Same as {pages[idx - 1]?.data?.title || "previous"}
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex min-h-[260px] flex-1 items-center justify-center overflow-hidden rounded-[30px] border-8 border-white bg-white shadow-2xl md:min-h-[320px] md:max-h-[70vh]">
            {selectedPage?.data?.image_url ? (
              <img
                src={selectedPage.data.image_url}
                alt="Event Preview"
                className="max-h-[66vh] w-auto max-w-full object-contain"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center px-8 text-center text-gray-400">
                <strong className="text-sm text-[#7f6172]">No preview available yet</strong>
                <span className="mt-2 text-xs leading-5">
                  Save the page details or return to the editor if this chapter needs a visual update.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="h-fit flex-1 overflow-visible rounded-[30px] bg-white p-6 shadow-xl md:p-8">
        <div className="mb-6 rounded-[26px] border border-[#f2dbe6] bg-[#fff8fc] p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#b07c98]">
                Current Chapter
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-tight text-gray-900">
                {selectedPage?.type === "event"
                  ? `${eventTypeName} Cover Details`
                  : selectedDisplayTitle}
              </h2>
              <p className="mt-2 text-sm leading-6 text-[#806777]">
                {selectedPage?.type === "event"
                  ? "These details shape the main invitation cover and overall event information."
                  : "Update this ceremony page directly, or link it to the previous ceremony when it should reuse the same schedule and venue."}
              </p>
            </div>
            <div className="rounded-2xl border border-[#ecd7e3] bg-white px-4 py-3 text-sm text-[#7c6172] shadow-sm">
              <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                Status
              </span>
              <strong className="mt-1 block text-[#3a2433]">{selectedPageStatus}</strong>
            </div>
          </div>
        </div>

        <div className="space-y-2" key={selectedPage?.data?.id || "form"}>
          {saveError ? (
            <div className="mb-4 rounded-2xl border border-[#f5c6d7] bg-[#fff4f8] px-4 py-3 text-sm text-[#9d295d]">
              {saveError}
            </div>
          ) : null}
          {saveNotice ? (
            <div className="mb-4 rounded-2xl border border-[#d9f0df] bg-[#f5fff7] px-4 py-3 text-sm text-[#1f7a41]">
              {saveNotice}
            </div>
          ) : null}
          {prefillNotice ? (
            <div className="mb-4 rounded-2xl border border-[#f1d6a8] bg-[#fffaf0] px-4 py-3 text-sm text-[#8a5a1d]">
              {prefillNotice}
            </div>
          ) : null}

          {selectedPage?.type === "subevent" ? (
            <div className="mb-4 rounded-2xl border border-[#f3d7e7] bg-[#fff7fb] p-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 accent-[#ff0099]"
                  checked={isSelectedSubeventLinked}
                  onChange={(e) => handleLinkedSubeventChange(selectedPage.data.id, e.target.checked)}
                />
                <div>
                  <div className="text-sm font-bold text-[#5d2242]">
                    Reuse previous ceremony details
                  </div>
                  <p className="mt-1 text-sm text-[#8d6680]">
                    Use this when multiple invitation chapters belong to the same function and do
                    not need a separate date, time, or venue.
                  </p>
                </div>
              </label>
            </div>
          ) : null}

          {isSelectedSubeventLinked ? (
            <div className="rounded-[24px] border border-[#efd7e4] bg-[#fff8fc] p-6">
              <div className="rounded-2xl bg-white p-5 shadow-sm">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#b07c98]">
                  Linked Page
                </p>
                <h3 className="mt-2 text-xl font-black text-gray-900">
                  This chapter will appear as {previousPageTitle}
                </h3>
                <p className="mt-3 text-sm leading-6 text-gray-500">
                  Date, time, and venue will be inherited from the previous chapter. Turn off the
                  option above any time if this page needs its own details.
                </p>
              </div>
            </div>
          ) : (
            <>
              <section className="rounded-[24px] border border-[#f1dde7] bg-white p-5 shadow-sm">
                <span className={sectionLabelClassName}>
                  {selectedPage?.type === "event" ? "Main Event Info" : "Chapter Info"}
                </span>
                <div className="space-y-3">
                  <label className={fieldLabelClassName} htmlFor="title">
                    {eventTypeFormConfig.eventNameLabel}:
                  </label>
                  <input
                    className={inputClassName}
                    id="title"
                    name="title"
                    value={selectedPage?.data?.title || ""}
                    onChange={handleChange}
                    placeholder="Event Name"
                  />

                  {selectedPage?.type === "event" ? (
                    <>
                      <label className={fieldLabelClassName} htmlFor="host_name">
                        {eventTypeFormConfig.hostNameLabel}:
                      </label>
                      <input
                        className={inputClassName}
                        id="host_name"
                        name="host_name"
                        value={selectedPage?.data?.host_name || ""}
                        onChange={handleChange}
                        placeholder="Host Name"
                      />
                    </>
                  ) : null}
                </div>
              </section>

              {selectedPage?.type === "event" && visibleMetadataFields.length > 0 ? (
                <section className="mt-4 rounded-[24px] border border-[#f1dde7] bg-white p-5 shadow-sm">
                  <span className={sectionLabelClassName}>{eventTypeFormConfig.metadataSectionTitle}</span>
                  <p className="mb-4 text-sm leading-6 text-[#806777]">
                    {eventTypeFormConfig.metadataSectionDescription}
                  </p>
                  <div className="space-y-3">
                      {visibleMetadataFields.map((field) => (
                        <React.Fragment key={field.key}>
                          <label className={fieldLabelClassName} htmlFor={field.key}>
                            {field.label}:
                          </label>
                          <input
                            className={inputClassName}
                            id={field.key}
                            name={field.key}
                            type={field.type || "text"}
                            value={structuredMetadata[field.key] || ""}
                            onChange={handleChange}
                            placeholder={field.placeholder}
                          />
                        </React.Fragment>
                      ))}
                  </div>
                </section>
              ) : null}

              <section className="mt-4 rounded-[24px] border border-[#f1dde7] bg-white p-5 shadow-sm">
                <span className={sectionLabelClassName}>Schedule</span>
                <p className="mb-4 text-sm leading-6 text-[#806777]">
                  {selectedPage?.type === "event"
                    ? "Use the primary event date and time for the main invitation."
                    : "Use this when the chapter has its own ceremony timing."}
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label className={fieldLabelClassName} htmlFor="event_date">
                      Event Start Date:
                    </label>
                    <input
                      className={inputClassName}
                      id="event_date"
                      type="date"
                      name="event_date"
                      value={selectedPage?.data?.event_date?.split("T")[0] || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className={fieldLabelClassName} htmlFor="event_time">
                      Event Start Time:
                    </label>
                    <select
                      className={inputClassName}
                      id="event_time"
                      name="event_time"
                      value={selectedPage?.data?.event_time || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select start time</option>
                      {TIME_OPTIONS.map((option) => (
                        <option key={`start-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label className={fieldLabelClassName} htmlFor="end_date">
                      Event End Date:
                    </label>
                    <input
                      className={inputClassName}
                      id="end_date"
                      type="date"
                      name="end_date"
                      value={selectedPage?.data?.end_date?.split("T")[0] || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className={fieldLabelClassName} htmlFor="end_time">
                      Event End Time:
                    </label>
                    <select
                      className={inputClassName}
                      id="end_time"
                      name="end_time"
                      value={selectedPage?.data?.end_time || ""}
                      onChange={handleChange}
                    >
                      <option value="">Optional end time</option>
                      {TIME_OPTIONS.map((option) => (
                        <option key={`end-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              <section className="mt-4 rounded-[24px] border border-[#f1dde7] bg-white p-5 shadow-sm">
                <span className={sectionLabelClassName}>Venue</span>
                <p className="mb-4 text-sm leading-6 text-[#806777]">
                  {selectedPage?.type === "event"
                    ? "Add the primary venue, address, landmark, and directions guests can trust."
                    : "Add a venue only if this chapter happens separately from the main event."}
                </p>
                <label className={fieldLabelClassName} htmlFor="location">
                  Venue / Address
                </label>
                <textarea
                  className={inputClassName}
                  id="location"
                  name="location"
                  rows={4}
                  maxLength={locationMaxLength}
                  value={selectedPage?.data?.location || ""}
                  onChange={handleChange}
                  placeholder={"Venue name\nStreet / area\nCity, State - PIN\nLandmark or directions note"}
                />
                <p className="mt-2 text-xs leading-5 text-[#8d6680]">
                  Tip: Indian venues often need a landmark, mandap/hall name, floor, or nearby temple/shop.
                  Add those details here so guests do not rely only on maps.{" "}
                  <span className="font-semibold">
                    {(selectedPage?.data?.location || "").length}/{locationMaxLength}
                  </span>
                </p>

                <div className="mt-4 flex flex-col gap-1.5">
                  <label className={fieldLabelClassName} htmlFor="location_map_link">
                    Map Link Optional
                  </label>
                  <input
                    className={inputClassName}
                    id="location_map_link"
                    name={selectedLocationMapKey}
                    maxLength={500}
                    value={structuredMetadata[selectedLocationMapKey] || ""}
                    onChange={(e) => handleMetadataValueChange(e.target.name, e.target.value)}
                    placeholder="Paste Google Maps, Apple Maps, or WhatsApp location link"
                  />
                </div>
              </section>
            </>
          )}
        </div>

        <div className="mt-8 flex flex-col gap-3 rounded-[24px] border border-[#f0dce6] bg-[#fff9fc] p-4">
          <div className="mb-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#b07c98]">
              Next Actions
            </p>
            <p className="mt-1 text-sm text-[#826878]">
              Save details now, then choose the guest-facing theme. Guest and group access to
              chapters is handled in Manage Guests after the invitation is ready.
            </p>
          </div>
          <button
            className="w-full rounded-2xl bg-[#ff0099] py-4 font-bold text-white shadow-lg shadow-[#ff0099]/20 transition-all hover:bg-black disabled:cursor-not-allowed disabled:opacity-70"
            onClick={() => handleSave(true)}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Choose Theme"}
          </button>
          <button
            className="w-full rounded-2xl bg-[#fff1f8] py-4 font-bold text-[#9d2967] transition-all hover:bg-[#ffe4f1]"
            onClick={() => handleSave(false)}
            disabled={isSaving}
          >
            Save Details
          </button>
        </div>
        </div>
      </div>
    </div>
    </div>
  );
};
